/**
 * EditorTabs — VS Code/WebStorm-style multi-file tab strip above the Monaco
 * editor. Reads the open list from openFilesStore; calls back into FilesTab
 * for the actual file load on switch. Capped at MAX_OPEN_TABS via the store.
 */
import { memo } from 'react';
import { X } from 'lucide-react';
import {
  useCurrentProjectActiveId,
  useCurrentProjectOpenFiles,
  useOpenFilesStore,
  type OpenFile,
} from '@/stores/openFilesStore';
import type { IconPack } from '@/lib/iconPacks/types';

interface EditorTabsProps {
  iconPack: IconPack;
  /** Called when the user clicks a tab. Triggers a content load. */
  onSwitchTo: (file: OpenFile) => void;
  /**
   * Called after a tab is closed. If the closed tab was active, the store
   * picks a neighbor as new active and this fires with that neighbor (or
   * null if no tabs remain) so the editor can load the new content.
   */
  onAfterClose: (newActive: OpenFile | null) => void;
}

export default memo(function EditorTabs({ iconPack, onSwitchTo, onAfterClose }: EditorTabsProps) {
  const open = useCurrentProjectOpenFiles();
  const activeId = useCurrentProjectActiveId();
  const closeFile = useOpenFilesStore((s) => s.closeFile);

  if (open.length === 0) return null;

  const handleClose = (e: React.MouseEvent, file: OpenFile) => {
    e.stopPropagation();
    if (file.dirty) {
      const ok = window.confirm(`"${file.name}" has unsaved changes. Close anyway?`);
      if (!ok) return;
    }
    const wasActive = file.id === activeId;
    closeFile(file.id);
    if (wasActive) {
      const store = useOpenFilesStore.getState();
      const pid = store.currentProjectId;
      const slice = pid ? store.byProject.get(pid) : undefined;
      const nextFile = slice?.activeId
        ? slice.open.find((f) => f.id === slice.activeId)
        : undefined;
      onAfterClose(nextFile ?? null);
    }
  };

  return (
    <div className="flex items-stretch border-b border-base-300 bg-base-200 overflow-x-auto scrollbar-thin">
      {open.map((file) => {
        const isActive = file.id === activeId;
        return (
          <div
            key={file.id}
            onClick={() => onSwitchTo(file)}
            onAuxClick={(e) => {
              // Middle-click closes — matches VS Code/Chrome.
              if (e.button === 1) handleClose(e, file);
            }}
            className={`group flex items-center gap-2 px-3 py-2 border-r border-base-300 cursor-pointer min-w-0 max-w-[16rem] transition-colors ${
              isActive
                ? 'bg-base-100 text-base-content'
                : 'text-base-content/70 hover:bg-base-300/50'
            }`}
            title={file.path}
          >
            <span className="flex-shrink-0">{iconPack.getFileIcon(file.name)}</span>
            <span className="text-sm truncate">{file.name}</span>
            {file.dirty && <span className="w-1.5 h-1.5 rounded-full bg-warning flex-shrink-0" />}
            <button
              onClick={(e) => handleClose(e, file)}
              className="ml-1 flex-shrink-0 opacity-0 group-hover:opacity-100 hover:bg-base-300 rounded p-0.5 transition-opacity"
              title="Close (Cmd/Ctrl+W)"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        );
      })}
    </div>
  );
});
